import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormHelperText, LinearProgress, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography, } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import useNotification from '../../../../core/hooks/use_notification';
import { getPodsNotificationData } from '../../../../core/utils/notification_utils';
import { getAllPodsAction, getAllPodsState } from '../../../../features/pod/get_all_pods';
import { isEmptyObject } from '../../../../core/utils/misc_utils';

const AddPodDestinationDialog = ({ formik }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getAllPods = useSelector(getAllPodsState);

    const [open, setOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [showMore, setShowMore] = useState(false);
    const [selectedPods, setSelectedPods] = useState([]);

    useEffect(() => {
        if (open && !getAllPods?.data) {
            dispatch(getAllPodsAction()).then((response) =>
                showErrorMessage(getPodsNotificationData(response))
            );
        }
    }, [dispatch, showErrorMessage, open, getAllPods?.data]);

    const onClose = () => setOpen(false);

    const onAddMultipleDestinations = (selectedDestinations) => {
        let currentDestinations = [...(formik?.values?.destinationsList || [])];
        let currentProducts = [...(formik?.values?.productsList || [])];
        let addedCount = 0;

        selectedDestinations.forEach((destination) => {
            const index = currentDestinations.findIndex(
                (d) => d?.destinationPodId === destination?.id
            );

            if (index === -1) {
                const lastItem = currentDestinations[currentDestinations.length - 1];

                currentDestinations.push({
                    position: currentDestinations.length ? lastItem.position + 1 : 0,
                    name: `${ destination?.indoorPartner?.name } (${ destination?.description }) - ${
                        destination?.device?.serviceId || 'brak urządzenia'
                    }`,
                    destinationPodId: destination?.id,
                    canBeRemoved: true,
                    warehouseId: undefined,
                    warehouseType: undefined,
                    isExpanded: true
                });

                currentProducts.push([]);
                addedCount++;
            }
        });

        if (addedCount > 0) {
            formik.setFieldValue('destinationsList', currentDestinations);
            formik.setFieldValue('productsList', currentProducts);
        }
    };

    const handleTogglePod = (pod) => {
        setSelectedPods((prevSelected) => {
            const isSelected = prevSelected.some((p) => p.id === pod.id);
            if (isSelected) {
                return prevSelected.filter((p) => p.id !== pod.id);
            } else {
                return [...prevSelected, pod];
            }
        });
    };

    const handleConfirmSelection = () => {
        if (selectedPods.length > 0) {
            onAddMultipleDestinations(selectedPods);

            handleClose();
        }
    };

    const handleClose = () => {
        setSelectedPods([]);
        onClose();
    };

    const filterTextLower = filterText.toLowerCase();

    const filteredPods = useMemo(() => {
        return getAllPods?.data?.filter((pod) => {
            if (!filterText) return true;
            return (
                pod?.indoorPartner?.name?.toLowerCase().includes(filterTextLower) ||
                pod?.description?.toLowerCase().includes(filterTextLower) ||
                (pod?.device?.serviceId || '').toLowerCase().includes(filterTextLower)
            );
        });
    }, [getAllPods?.data, filterText, filterTextLower]);

    const defaultOptions =
        filteredPods?.filter((pod) => pod?.device?.serviceId != null) || [];
    const extraOptions =
        filteredPods?.filter((pod) => pod?.device?.serviceId == null) || [];

    return (
        <>
            <Box sx={ { width: '300px', mt: 4 } } onClick={ () => setOpen(true) }>
                <Stack direction="row" alignItems="center" gap={ 1 } sx={ { cursor: 'pointer' } }>
                    <AddCircleIcon color="primary"/>
                    <Typography fontWeight="bold">Dodaj kolejne lokalizacje</Typography>
                </Stack>
                <FormHelperText error>
                    { !isEmptyObject(formik?.errors?.destinationsList) &&
                        formik?.errors?.destinationsList }
                </FormHelperText>
            </Box>

            <Dialog
                open={ open }
                onClose={ handleClose }
                fullWidth
                maxWidth="md"
                PaperProps={ {
                    sx: {
                        width: { xs: '100%', sm: '90%', md: '800px' },
                        height: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                } }
            >
                <DialogTitle>Wybierz Punkty Sprzedaży</DialogTitle>

                <Box
                    sx={ {
                        px: 2,
                        pb: 1,
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                    } }
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Szukaj partnera, opis lub urządzenie..."
                        value={ filterText }
                        onChange={ (e) => setFilterText(e.target.value) }
                    />
                </Box>

                <DialogContent sx={ { flex: 1, overflowY: 'auto' } }>
                    { getAllPods?.loading && <LinearProgress sx={ { mb: 2 } }/> }
                    <List>
                        { defaultOptions.map((pod) => (
                            <ListItem
                                key={ pod?.id }
                                button
                                onClick={ () => handleTogglePod(pod) }
                                sx={ {
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'slate.100',
                                    },
                                } }
                            >
                                <ListItemIcon>
                                    <StorefrontIcon/>
                                </ListItemIcon>
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={ selectedPods.some((p) => p.id === pod.id) }
                                        tabIndex={ -1 }
                                        disableRipple
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={ `${ pod?.indoorPartner?.name || 'Brak partnera' } - ${
                                        pod?.device?.serviceId || 'brak urządzenia'
                                    }` }
                                    secondary={ `${ pod?.description || '' } • ${ pod?.address || '' }` }
                                />
                            </ListItem>
                        )) }

                        { extraOptions.length > 0 && !showMore && (
                            <ListItemButton
                                onClick={ () => setShowMore(true) }
                                sx={ { justifyContent: 'center' } }
                            >
                                ➕ Pokaż więcej
                            </ListItemButton>
                        ) }

                        { showMore &&
                            extraOptions.map((pod) => (
                                <ListItem
                                    key={ pod?.id }
                                    button
                                    onClick={ () => handleTogglePod(pod) }
                                >
                                    <ListItemIcon>
                                        <LocationOnIcon/>
                                    </ListItemIcon>
                                    <ListItemIcon>
                                        <Checkbox
                                            edge="start"
                                            checked={ selectedPods.some((p) => p.id === pod.id) }
                                            tabIndex={ -1 }
                                            disableRipple
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={ `${ pod?.indoorPartner?.name || 'Brak partnera' } - ${
                                            pod?.device?.serviceId || 'brak urządzenia'
                                        }` }
                                        secondary={ `${ pod?.description || '' } • ${ pod?.address || '' }` }
                                    />
                                </ListItem>
                            )) }

                        { showMore && extraOptions.length > 0 && (
                            <ListItemButton
                                onClick={ () => setShowMore(false) }
                                sx={ { justifyContent: 'center' } }
                            >
                                ➖ Pokaż mniej
                            </ListItemButton>
                        ) }
                    </List>
                </DialogContent>

                <Divider/>

                <DialogActions sx={ { px: 3, py: 2, justifyContent: 'space-between' } }>
                    <Typography variant="body2" color="textSecondary">
                        Wybrano: { selectedPods.length }
                    </Typography>
                    <Box>
                        <Button onClick={ handleClose } variant="outlined" sx={ { mr: 1 } }>
                            Anuluj
                        </Button>
                        <Button
                            onClick={ handleConfirmSelection }
                            variant="contained"
                            color="primary"
                            disabled={ selectedPods.length === 0 }
                        >
                            Dodaj zaznaczone
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddPodDestinationDialog;
